import { Box, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  PaymentCondition,
  disablePaymentCondition,
  updateSchambeckUserTableConfig,
  useData,
} from 'shared';
import { convertPaymentConditionsIntoTableEntries } from '../../utils/invoice/Invoice.utils';
import { useUser } from '../../components/UserContext';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const PaymentConditionOverview: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { axios, user, updateContextUser } = useUser();
  const [paymentConditions, setPaymentConditions] = useState<PaymentCondition[]>([]);
  const { data: loadedPaymentConditions } = useData('PAYMENT_CONDITION_ALL', {
    config: { fallbackData: [] },
  });

  // Hook to set paymentConditions after they are loaded
  useEffect(() => setPaymentConditions(loadedPaymentConditions), [loadedPaymentConditions]);

  /**
   * Helper method to deactivate paymentConditions
   * @param id of the condition to deactivate
   */
  const handleDisablePaymentCondition = useCallback(
    (id: string): void => {
      disablePaymentCondition(axios, id).then((disabledCondition) => {
        if (disabledCondition)
          setPaymentConditions((prevCondition) =>
            prevCondition.filter((condition) => condition.id !== id),
          );
      });
    },
    [axios],
  );
  /**
   * useMemo to keep the tableRows up to date
   */
  const rows: TableRow[] = useMemo(
    () =>
      paymentConditions
        ? convertPaymentConditionsIntoTableEntries(
            paymentConditions,
            navigate,
            handleDisablePaymentCondition,
          )
        : [],
    [handleDisablePaymentCondition, navigate, paymentConditions],
  );

  return (
    <>
      <TopBar title={t('pages.paymentCondition.overview.topBarHeadline')}>
        <IconComponent type={IconType.ADD} onClick={() => navigate('/payment/edit')} />
      </TopBar>
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.PAYMENT_CONDITION_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.PAYMENT_CONDITION_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          rows={rows}
          header={
            t('pages.paymentCondition.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
